import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { notifyPing } from "@/lib/notify";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { LayoutDashboard, Store, Users, FileText, FolderKanban, UserCog, CreditCard, HardHat, LogOut, Home, Image, TrendingUp, Clock, Receipt, Calculator, Landmark, MoreHorizontal, Globe, Inbox, Layout, Bell, MessageSquare } from "lucide-react";

const items = [
  { to: "/dashboard", end: true, key: "dash_overview", icon: LayoutDashboard },
  { to: "/dashboard/business", key: "dash_profile", icon: Store },
  { to: "/dashboard/site", key: "dash_site", icon: Layout, feat: "business_site" },
  { to: "/dashboard/leads", key: "dash_leads", icon: Inbox, feat: "business_site" },
  { to: "/dashboard/gallery", key: "dash_gallery", icon: Image, feat: "gallery" },
  { to: "/dashboard/clients", key: "dash_clients", icon: Users, feat: "clients" },
  { to: "/dashboard/invoices", key: "dash_invoices", icon: FileText, feat: "invoicing" },
  { to: "/dashboard/receivables", key: "dash_receivables", icon: Landmark, feat: "receivables" },
  { to: "/dashboard/projects", key: "dash_projects", icon: FolderKanban, feat: "projects" },
  { to: "/dashboard/timesheets", key: "dash_timesheets", icon: Clock, feat: "timesheets" },
  { to: "/dashboard/payroll", key: "dash_payroll", icon: Receipt, feat: "payroll" },
  { to: "/dashboard/profitability", key: "dash_profitability", icon: TrendingUp, feat: "projects" },
  { to: "/dashboard/accounting", key: "dash_accounting", icon: Calculator, feat: "accounting" },
  { to: "/dashboard/employees", key: "dash_employees", icon: UserCog, feat: "employees" },
  { to: "/dashboard/subscription", key: "dash_subscription", icon: CreditCard },
  { to: "/dashboard/notifications", key: "dash_notifications", icon: Bell },
  { to: "/dashboard/messages", key: "dash_messages", icon: MessageSquare },
];

// keys shown as direct shortcuts on the mobile bottom bar
const bottomKeys = ["dash_overview", "dash_clients", "dash_invoices", "dash_projects"];

export default function DashboardLayout() {
  const { t, lang, toggle, logo } = useLang();
  const { logout } = useAuth();
  const location = useLocation();
  const [ent, setEnt] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const prevChat = useRef(null);

  const loadUnread = () => api.get("/businesses/notifications/unread-count").then((r) => setUnread(r.data.count || 0)).catch(() => {});
  const loadChatUnread = () => api.get("/chat/unread-total").then((r) => {
    const c = r.data.count || 0;
    if (prevChat.current !== null && c > prevChat.current) { notifyPing(); toast.message(lang === "es" ? "Nuevo mensaje de un cliente" : "New message from a client"); }
    prevChat.current = c;
    setChatUnread(c);
  }).catch(() => {});

  useEffect(() => {
    api.get("/entitlements").then((r) => setEnt(r.data)).catch(() => setEnt({ features: {} }));
    loadUnread();
    loadChatUnread();
    const iv = setInterval(loadUnread, 30000);
    const ivc = setInterval(loadChatUnread, 10000);
    const onUpsell = (e) => toast.error(e.detail || "Función no incluida en tu plan", {
      action: { label: "Mejorar plan", onClick: () => { window.location.href = "/dashboard/subscription"; } },
    });
    window.addEventListener("plan-upsell", onUpsell);
    const onChatRead = () => loadChatUnread();
    window.addEventListener("chat-read", onChatRead);
    return () => { window.removeEventListener("plan-upsell", onUpsell); window.removeEventListener("chat-read", onChatRead); clearInterval(iv); clearInterval(ivc); };
  }, []);

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  const handleMoreOpen = (open) => {
    setMoreOpen(open);
    if (open && unread > 0) {
      api.post("/businesses/notifications/mark-read").then(() => setUnread(0)).catch(() => {});
    }
  };

  const allowed = (it) => !it.feat || !ent || ent.features?.[it.feat];
  const visible = items.filter(allowed);
  const bottomItems = visible.filter((it) => bottomKeys.includes(it.key));
  const moreItems = visible.filter((it) => !bottomKeys.includes(it.key));
  const current = [...items].filter((it) => it.to === location.pathname)[0] || items[0];

  return (
    <div className="flex min-h-screen bg-slate-100">
      {/* desktop sidebar */}
      <aside className="fixed hidden h-screen w-60 flex-col border-r border-slate-800 bg-secondary text-slate-300 lg:flex">
        <Link to="/" className="flex h-16 items-center gap-2 border-b border-slate-800 px-6">
          {logo ? (
            <img src={logo} alt={t("brand")} className="h-11 w-auto max-w-[200px] object-contain" data-testid="sidebar-logo" />
          ) : (
            <>
              <span className="flex h-8 w-8 items-center justify-center rounded-sm bg-primary text-white"><HardHat className="h-4 w-4" /></span>
              <span className="font-head text-lg font-extrabold text-white">{t("brand")}</span>
            </>
          )}
        </Link>
        <nav className="flex-1 space-y-1 p-3">
          {visible.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} className={({ isActive }) => `flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-primary text-white" : "text-slate-400 hover:bg-slate-800 hover:text-white"}`} data-testid={`sidebar-${it.key}`}>
              <it.icon className="h-4 w-4" /> <span className="flex-1">{t(it.key)}</span>
              {it.key === "dash_messages" && chatUnread > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white" data-testid="sidebar-messages-badge">{chatUnread > 9 ? "9+" : chatUnread}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="space-y-1 border-t border-slate-800 p-3">
          <button onClick={toggle} className="flex w-full items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-semibold text-slate-400 hover:bg-slate-800 hover:text-white" data-testid="dash-lang-toggle">
            {lang === "es" ? "English" : "Español"}
          </button>
          <Link to="/" className="flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-semibold text-slate-400 hover:bg-slate-800 hover:text-white"><Home className="h-4 w-4" /> {t("nav_directory")}</Link>
          <button onClick={logout} className="flex w-full items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-semibold text-slate-400 hover:bg-slate-800 hover:text-white" data-testid="dash-logout-btn"><LogOut className="h-4 w-4" /> {t("nav_logout")}</button>
        </div>
      </aside>

      <div className="min-w-0 flex-1 lg:ml-60">
        {/* mobile clean header */}
        <div className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-800 bg-secondary px-4 shadow-md lg:hidden" data-testid="mobile-header">
          <div className="flex items-center gap-2">
            {logo ? (
              <img src={logo} alt={t("brand")} className="h-9 w-auto max-w-[150px] object-contain" />
            ) : (
              <span className="flex h-7 w-7 items-center justify-center rounded-sm bg-primary text-white"><HardHat className="h-4 w-4" /></span>
            )}
            <span className="font-head text-base font-extrabold text-white">{t(current.key)}</span>
          </div>
          <button onClick={toggle} className="flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1 text-xs font-semibold text-slate-300" data-testid="mobile-lang-toggle">
            <Globe className="h-3.5 w-3.5" /> {lang === "es" ? "EN" : "ES"}
          </button>
        </div>

        <div className="p-4 pb-24 sm:p-6 lg:pb-6">
          <Outlet />
        </div>
      </div>

      {/* mobile bottom app nav */}
      <nav className="fixed inset-x-0 bottom-0 z-40 flex items-stretch border-t border-slate-800 bg-secondary lg:hidden" data-testid="mobile-bottom-nav">
        {bottomItems.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end} className={({ isActive }) => `flex flex-1 flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-semibold transition-colors ${isActive ? "text-primary" : "text-slate-400"}`} data-testid={`bottomnav-${it.key}`}>
            <it.icon className="h-5 w-5" />
            <span className="truncate px-1">{t(it.key)}</span>
          </NavLink>
        ))}
        <Sheet open={moreOpen} onOpenChange={handleMoreOpen}>
          <SheetTrigger asChild>
            <button className="relative flex flex-1 flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-semibold text-slate-400" data-testid="bottomnav-more">
              <span className="relative">
                <MoreHorizontal className="h-5 w-5" />
                {unread > 0 && (
                  <span className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white" data-testid="notif-badge">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </span>
              <span>{t("dash_more")}</span>
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-2xl border-slate-800 bg-secondary text-slate-300" data-testid="mobile-more-sheet">
            <SheetHeader className="mb-4 text-left">
              <SheetTitle className="text-white">{t("dash_menu")}</SheetTitle>
            </SheetHeader>
            <div className="grid grid-cols-3 gap-3">
              {moreItems.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setMoreOpen(false)} className={({ isActive }) => `relative flex flex-col items-center gap-2 rounded-lg border p-3 text-center text-xs font-semibold transition-colors ${isActive ? "border-primary bg-primary/10 text-primary" : "border-slate-800 bg-slate-800/40 text-slate-300"}`} data-testid={`more-${it.key}`}>
                  <it.icon className="h-5 w-5" />
                  <span className="leading-tight">{t(it.key)}</span>
                  {it.key === "dash_messages" && chatUnread > 0 && (
                    <span className="absolute right-1.5 top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white" data-testid="more-messages-badge">{chatUnread > 9 ? "9+" : chatUnread}</span>
                  )}
                </NavLink>
              ))}
            </div>
            <div className="mt-5 space-y-1 border-t border-slate-800 pt-4">
              <Link to="/" onClick={() => setMoreOpen(false)} className="flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-semibold text-slate-300 hover:bg-slate-800 hover:text-white"><Home className="h-4 w-4" /> {t("nav_directory")}</Link>
              <button onClick={() => { setMoreOpen(false); logout(); }} className="flex w-full items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-semibold text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="more-logout-btn"><LogOut className="h-4 w-4" /> {t("nav_logout")}</button>
            </div>
          </SheetContent>
        </Sheet>
      </nav>
    </div>
  );
}
